import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { getImage } from '../utils/imageImports'; // Import our image utility
import './css/cards.css';

// Helper function for card tilt effect
const calculateTilt = (e, rect) => {
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const percentX = (e.clientX - centerX) / (rect.width / 2);
  const percentY = (e.clientY - centerY) / (rect.height / 2);
  return { x: percentY * -7, y: percentX * 7, z: 20 };
};

// Card content animation variants
const cardContentVariants = {
  collapsed: { 
    height: 0,
    opacity: 0
  },
  expanded: { 
    height: 'auto',
    opacity: 1,
    transition: {
      height: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        duration: 0.4
      },
      opacity: {
        duration: 0.25,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    }
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      height: {
        type: "spring",
        stiffness: 400,
        damping: 40,
        duration: 0.3
      },
      opacity: {
        duration: 0.2
      }
    }
  }
};

// Card animation variants
const variants = {
  hidden: { 
    opacity: 0,
    y: 50,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
      delay: (index) => Math.min(index * 0.1, 0.8)  // Fixed issue with delay calculation
    }
  },
  hover: {
    y: -10,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20
    }
  }
};

// Individual Card Component
const AttractionCard = ({ title, description, image, link, index, isMobileView, id }) => {
  const cardRef = useRef(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, z: 10 });
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimerRef = useRef(null);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
    initialInView: true // Always start as visible
  });

  // Get the proper image path using our utility
  const processedImage = getImage(image);

  // Smoother mouse move handling
  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    
    // Use requestAnimationFrame for smoother tilt effect
    requestAnimationFrame(() => {
      const rect = cardRef.current.getBoundingClientRect();
      setTransform(calculateTilt(e, rect));
    });
  };

  const handleMouseEnter = () => {
    // Clear any existing timer
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    
    // Set a small delay before applying hover effect
    const delayTime = isMobileView ? 80 : 30;
    
    hoverTimerRef.current = setTimeout(() => {
      setIsHovered(true);
    }, delayTime);
  };
  
  const handleMouseLeave = () => {
    // Clear any existing timer
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }
    
    // Set a delay before removing hover effect
    const delayTime = isMobileView ? 100 : 50;
    
    hoverTimerRef.current = setTimeout(() => {
      setIsHovered(false);
      setTransform({ x: 0, y: 0, z: 10 });
    }, delayTime);
  };

  // Create a link to the ThingsToDo page with the attraction ID
  const getAttractionLink = () => {
    // If it's an attraction with an ID, link to ThingsToDo page with that ID
    if (id) {
      return `/todo?attraction=${id}`;
    }
    // Otherwise use the provided link or default to info page
    return link || '/info';
  };

  return (
    <div
      ref={ref}
      className="card-wrapper"
    >
      <div 
        ref={cardRef}
        className={`card ${isHovered ? 'hovered' : ''}`}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ 
          transform: `perspective(1000px) rotateX(${transform.x}deg) rotateY(${transform.y}deg) scale3d(1, 1, 1)`,
          transition: 'transform 0.35s cubic-bezier(0.25, 0.1, 0.25, 1.0)'
        }}
      >
        <div className="card-image-container">
          <img 
            src={processedImage} 
            alt={title} 
            className="card-image"
          />
        </div>

        <div className="card-content-container">
          <div 
            className="card-content"
          >
            <div className="card-text-background">
              <h3
                className="card-title"
              >
                {title}
              </h3>
            
              <div className="card-description">
                <p>{description}</p>
              </div>
            </div>

            <div className="card-button-container">
              <div>
                <Link to={getAttractionLink()} className="card-link">Learn More</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cards Container Component
const Cards = ({ attractions, isVisible }) => {
  const [visibleCount, setVisibleCount] = useState(8);
  const [isMobile, setIsMobile] = useState(false);
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activePage, setActivePage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);

  // Detect screen size to switch between mobile/desktop layouts
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1450);
    };
    
    // Check initially
    checkMobile();
    
    // Add resize listener
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate how many items to show per row based on screen size
  const getItemsPerRow = () => {
    const width = window.innerWidth;
    if (width >= 1450) return 4; // Desktop: 4 items per row
    if (width >= 900) return 3;  // Medium screens: 3 items per row
    if (width >= 600) return 2;  // Small screens: 2 items per row
    return 1;                    // Mobile: 1 item per row
  };

  // Calculate total pages for pagination dots
  useEffect(() => {
    if (isMobile && scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.clientWidth;
      const totalWidth = scrollContainerRef.current.scrollWidth;
      const pages = Math.ceil(totalWidth / containerWidth);
      setTotalPages(Math.max(1, pages));
    }
  }, [isMobile, attractions]);

  // Update active page based on scroll position
  const updateActivePage = () => {
    if (isMobile && scrollContainerRef.current) {
      const containerWidth = scrollContainerRef.current.clientWidth;
      const scrollPosition = scrollContainerRef.current.scrollLeft;
      
      // Calculate the page based on scroll position
      const newPage = Math.round(scrollPosition / containerWidth);
      
      // Get the maximum possible page index
      const maxPageIndex = Math.max(0, totalPages - 1);
      
      // Check if we're at the end of scrolling (within a small margin)
      const isAtEnd = scrollPosition + containerWidth >= scrollContainerRef.current.scrollWidth - 10;
      
      // Set to max page if at the end
      setActivePage(isAtEnd ? maxPageIndex : newPage);
    }
  };

  // Load more button handler
  const handleLoadMore = () => {
    // Navigate to the To Do page instead of loading more cards
    window.location.href = '/todo';
  };

  // Check if we can scroll in either direction
  const checkScrollability = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    // Can scroll left if scrollLeft > 10
    setCanScrollLeft(container.scrollLeft > 10); // Small threshold to prevent flickering
    
    // Can scroll right if we haven't reached the end
    setCanScrollRight(
      container.scrollWidth > container.clientWidth &&
      container.scrollLeft < (container.scrollWidth - container.clientWidth - 10) // Adding a small buffer
    );
    
    // Update the active page for dots
    updateActivePage();
  };

  // Update scroll buttons visibility when container is scrolled or resized
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (isMobile && container) {
      // Allow initial animations to complete
      setTimeout(() => {
        setIsInitialLoad(false);
      }, 500);
      
      // Initial check
      checkScrollability();
      
      // Event listeners for scroll and resize
      container.addEventListener('scroll', checkScrollability);
      window.addEventListener('resize', checkScrollability);
      
      // Cleanup
      return () => {
        container.removeEventListener('scroll', checkScrollability);
        window.removeEventListener('resize', checkScrollability);
      };
    }
  }, [isMobile]);

  // Scroll horizontally with enhanced animation
  const scrollHorizontally = (direction) => {
    const container = scrollContainerRef.current;
    if (!container || isScrolling) return;
    
    // Set scrolling state to prevent multiple clicks
    setIsScrolling(true);
    
    // Calculate the scroll distance (one container width for smoother pagination)
    const scrollDistance = container.clientWidth * (direction === 'left' ? -0.95 : 0.95);
    
    // Start position
    const startPosition = container.scrollLeft;
    // Target position
    const targetPosition = startPosition + scrollDistance;
    // Start time
    const startTime = performance.now();
    // Duration in milliseconds - slightly reduced for more responsive feel
    const duration = 500;
    
    // Use requestAnimationFrame for smoother animation
    let rafId;
    
    // Animation function with optimized easing
    const animateScroll = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      
      if (elapsedTime < duration) {
        // easeOutQuint easing - smoother than cubic for scrolling
        const t = elapsedTime / duration;
        const ts = 1 - Math.pow(1 - t, 5);
        
        // Apply the scroll position - use transform for smoother animation
        container.scrollLeft = startPosition + (scrollDistance * ts);
        
        // Continue animation
        rafId = requestAnimationFrame(animateScroll);
      } else {
        // Ensure we reach exactly the target position
        container.scrollLeft = targetPosition;
        
        // Reset scrolling state
        setIsScrolling(false);
        
        // Check scroll buttons after animation completes
        checkScrollability();
      }
    };
    
    // Start the animation
    rafId = requestAnimationFrame(animateScroll);
    
    // Cleanup function in case we need to cancel
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        setIsScrolling(false);
      }
    };
  };

  // Scroll to a specific page (when clicking dots)
  const scrollToPage = (pageIndex) => {
    const container = scrollContainerRef.current;
    if (!container || isScrolling) return;
    
    // Set scrolling state
    setIsScrolling(true);
    
    // Calculate target position
    const containerWidth = container.clientWidth;
    const targetPosition = pageIndex * containerWidth;
    
    // Start position
    const startPosition = container.scrollLeft;
    // Start time
    const startTime = performance.now();
    // Duration in milliseconds - slightly reduced for responsiveness
    const duration = 500;
    
    // Use requestAnimationFrame for smoother animation
    let rafId;
    
    // Animation function with optimized easing
    const animateScroll = (currentTime) => {
      const elapsedTime = currentTime - startTime;
      
      if (elapsedTime < duration) {
        // easeOutQuint easing - smoother than cubic for scrolling
        const t = elapsedTime / duration;
        const ts = 1 - Math.pow(1 - t, 5);
        
        // Calculate the distance to scroll
        const scrollDistance = targetPosition - startPosition;
        
        // Apply the scroll position
        container.scrollLeft = startPosition + (scrollDistance * ts);
        
        // Continue animation
        rafId = requestAnimationFrame(animateScroll);
      } else {
        // Ensure we reach exactly the target position
        container.scrollLeft = targetPosition;
        
        // Update active page and reset scrolling state
        setActivePage(pageIndex);
        setIsScrolling(false);
        
        // Check scroll buttons after animation completes
        checkScrollability();
      }
    };
    
    // Start the animation
    rafId = requestAnimationFrame(animateScroll);
    
    // Cleanup function in case we need to cancel
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        setIsScrolling(false);
      }
    };
  };

  // Horizontal scroll with mouse wheel for mobile view - with improved smoothness
  useEffect(() => {
    if (isMobile && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      let lastScrollTime = 0;
      let scrollTimeoutId = null;
      let accumulatedDelta = 0;
      const scrollThreshold = 5; // Minimum threshold before applying scroll
      
      const smoothWheelScroll = (e) => {
        e.preventDefault();
        
        // Calculate a smoother delta based on wheel movement
        const delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
        const currentTime = performance.now();
        
        // Throttle scroll events for smoother experience
        if (currentTime - lastScrollTime > 16) { // ~60fps
          // Apply accumulated delta with dampening for smoother feel
          accumulatedDelta += delta * 0.75;
          
          // Apply scroll if above threshold
          if (Math.abs(accumulatedDelta) > scrollThreshold) {
            // Apply scroll with inertia effect
            container.scrollLeft += accumulatedDelta;
            
            // Reset accumulated delta after applying
            accumulatedDelta = 0;
            
            // Check scrollability after scroll
            checkScrollability();
          }
          
          lastScrollTime = currentTime;
        }
        
        // Clear previous timeout
        if (scrollTimeoutId) {
          clearTimeout(scrollTimeoutId);
        }
        
        // Apply any remaining delta after small delay
        scrollTimeoutId = setTimeout(() => {
          if (accumulatedDelta !== 0) {
            container.scrollLeft += accumulatedDelta;
            accumulatedDelta = 0;
            checkScrollability();
          }
        }, 50);
      };
      
      container.addEventListener('wheel', smoothWheelScroll, { passive: false });
      
      return () => {
        container.removeEventListener('wheel', smoothWheelScroll);
        if (scrollTimeoutId) {
          clearTimeout(scrollTimeoutId);
        }
      };
    }
  }, [isMobile]);

  return (
    <div className="cards-container">
      {isMobile && (
        <button 
          className={`scroll-arrow scroll-left ${!canScrollLeft ? 'hidden' : ''}`}
          onClick={() => scrollHorizontally('left')}
          aria-label="Scroll left"
          disabled={isScrolling}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
        </button>
      )}

      <div 
        className={`cards ${isMobile ? 'mobile-cards' : ''}`}
        ref={scrollContainerRef}
      >
        {attractions.slice(0, isMobile ? attractions.length : visibleCount).map((attraction, index) => (
          <AttractionCard
            key={attraction.id || index}
            {...attraction}
            index={index}
            isMobileView={isMobile}
          />
        ))}
      </div>

      {isMobile && (
        <button 
          className={`scroll-arrow scroll-right ${!canScrollRight ? 'hidden' : ''} ${isInitialLoad ? 'preload' : ''}`}
          onClick={() => scrollHorizontally('right')}
          aria-label="Scroll right"
          disabled={isScrolling}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
          </svg>
        </button>
      )}

      {!isMobile && attractions.length > visibleCount && (
        <div className="load-more-container">
          <motion.button 
            className="load-more-btn"
            onClick={handleLoadMore}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Load More
          </motion.button>
        </div>
      )}

      {isMobile && totalPages > 1 && (
        <div className="scrollbar-indicator">
          {Array.from({ length: totalPages }).map((_, index) => (
            <div 
              key={index} 
              className={`scrollbar-dot ${index === activePage ? 'active' : ''} ${Math.abs(index - activePage) <= 2 ? 'visible' : 'distant'}`}
              onClick={() => scrollToPage(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export { Cards, AttractionCard };